import staticImageLoader from "@lib/staticImageLoader";
import { Box, Typography } from "@mui/material";
import animateScrollTo from "animated-scroll-to";
import { useTemplate } from "containers/templateDetail/store";
import Image from "next/image";
import EmtpyImage from "public/image/product-review.svg";
import * as React from "react";
import { useReviews } from "stores/useStore";
import { Container } from "../index";
import List from "./list";
import Summary from "./summary";
import WriteReview from "./writeReview";

export default function Reviews(): React.ReactElement {
  const { id: templateID } = useTemplate();
  const [page, setPage] = React.useState(1);
  const reviewRef = React.useRef(null);

  const {
    reviews: cacheReviews,
    isLoading,
    mutate,
    pagination: cachePagination,
  } = useReviews(templateID, page);

  const [reviews, setReviews] = React.useState(cacheReviews);
  const [pagination, setPagination] = React.useState(cachePagination);

  React.useEffect(() => {
    if (cacheReviews) {
      setReviews(cacheReviews);
    }
  }, [cacheReviews]);

  React.useEffect(() => {
    if (cachePagination) {
      setPagination(cachePagination);
    }
  }, [cachePagination]);

  const refreshList = () => {
    mutate();
  };

  const handlePageChange = (page: number) => {
    animateScrollTo(reviewRef.current.offsetTop - 23, {
      maxDuration: 250,
    });
    setPage(page);
  };

  return (
    <Box sx={{ mt: "-55px", pt: "55px" }} id="reviews" ref={reviewRef}>
      <Container>
        <Box
          sx={{
            display: "flex",
            mt: 4,
            flexDirection: { xs: "column", md: "row" },
          }}
        >
          <Box
            sx={{
              width: { xs: "100%", md: 350 },
              pr: { xs: 0, md: 4 },
            }}
          >
            <Box
              sx={{
                position: { xs: "relative", md: "sticky" },
                top: { xs: 0, md: 88 },
                pb: 3,
                mb: { xs: 3, md: 0 },
                borderBottom: {
                  xs: "1px solid",
                  md: "0px",
                },
                borderBottomColor: {
                  xs: "divider",
                },
              }}
            >
              <Summary total={pagination?.total} />
              <WriteReview onSuccess={refreshList} />
            </Box>
          </Box>
          <Box sx={{ flex: 1, position: "relative" }}>
            {reviews && reviews.length === 0 && page === 1 && (
              <Box sx={{ pb: 4, mt: 1 }}>
                <Typography variant="body1" align="center">
                  Would you like to leave one?
                </Typography>
                <Typography variant="body1" align="center">
                  There are no submitted reviews for this product right now.
                </Typography>
                <Box sx={{ width: "100%", textAlign: "center" }}>
                  <Image
                    src={EmtpyImage}
                    width={600}
                    height={408}
                    loader={staticImageLoader}
                    alt="No review yet"
                  />
                </Box>
              </Box>
            )}
            {reviews && (reviews.length > 0 || page > 1) && (
              <List
                handlePageChange={handlePageChange}
                reviews={reviews}
                pagination={pagination}
                isLoading={isLoading}
                refreshList={refreshList}
              />
            )}
          </Box>
        </Box>
      </Container>
    </Box>
  );
}
